import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";

import { CourseGroupApi, coursegroupsApi } from "api";
import { QUERIES_KEYS } from "queries";
import { useQueryClient } from "@tanstack/react-query";
import { useToaster } from "utils";
import {
  PaziaButton,
  PaziaErrorMessage,
  PaziaFormButton,
  PaziaInput,
  PaziaSelect,
} from "components/atoms";
import { useAdminUsersOptions } from "api/admin-users/useAdminUsers";
import { DeleteButton } from "components/atoms/Buttons";

interface CourseGroupContactPersonsProps {
  courseGroup: CourseGroupApi;
}
export const CourseGroupContactPersons = ({ courseGroup }: CourseGroupContactPersonsProps) => {
  const { showError, showSuccess } = useToaster();
  const { loading: loadingAdmins, options: adminOptions } = useAdminUsersOptions();
  const methods = useForm<CourseGroupContactPersonsValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      contactPersons: courseGroup.contactPeople?.map((contactPerson) => ({
        area: contactPerson.area,
        phone: contactPerson.phone,
        email: contactPerson.email,
        userId: contactPerson.userId,
      })),
    },
  });
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: QUERIES_KEYS.COURSE_GROUP_BY_ID(courseGroup.id),
    });
  };

  const onSubmit = async (data: CourseGroupContactPersonsValues) => {
    try {
      await coursegroupsApi.updateContactPersons(courseGroup.id, data.contactPersons);
      showSuccess("Pomyślnie zapisano.");
      invalidate();
    } catch (e) {
      showError("Nie udało się zapisać.");
    }
  };

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "contactPersons",
  });
  const {
    formState: { errors },
  } = methods;
  const { handleSubmit } = methods;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="mb-6">
        <h2 className="mb-2 text-xl font-semibold text-slate-800 dark:text-slate-100">Osoby kontaktowe</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Zarządzaj osobami kontaktowymi dla grupy kursów ({fields.length}{" "}
          {fields.length === 1 ? "osoba" : fields.length < 5 ? "osoby" : "osób"})
        </p>
      </div>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            {fields.length > 0 ? (
              fields.map((field, index) => (
                <div
                  key={field.id}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-700/50"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Osoba kontaktowa #{index + 1}
                    </h3>
                    <DeleteButton
                      onClick={() => remove(index)}
                      className="h-8 w-8 rounded-md bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40"
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2">
                    <PaziaSelect
                      name={`contactPersons[${index}].${CONTACT_PERSONS_FORM_FIELDS.USER_ID}`}
                      label="Wybierz osobę"
                      options={adminOptions}
                      isClearable={false}
                      required
                      placeholder="Wybierz użytkownika..."
                      isLoading={loadingAdmins}
                    />
                    <PaziaInput
                      required
                      name={`contactPersons[${index}].${CONTACT_PERSONS_FORM_FIELDS.AREA}`}
                      label="Obszar odpowiedzialności"
                      placeholder="np. Rekrutacja, Wsparcie techniczne..."
                    />
                    <PaziaInput
                      required
                      name={`contactPersons[${index}].${CONTACT_PERSONS_FORM_FIELDS.PHONE}`}
                      label="Numer telefonu"
                      placeholder="np. +48 123 456 789"
                    />
                    <PaziaInput
                      required
                      name={`contactPersons[${index}].${CONTACT_PERSONS_FORM_FIELDS.EMAIL}`}
                      label="Adres email"
                      placeholder="np. kontakt@example.com"
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center dark:border-slate-600 dark:bg-slate-700/50">
                <svg
                  className="mx-auto mb-2 h-8 w-8 text-slate-400 dark:text-slate-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Nie dodano jeszcze żadnych osób kontaktowych
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  Kliknij "Dodaj osobę kontaktową" aby rozpocząć
                </p>
              </div>
            )}

            {/* Error messages */}
            {errors?.contactPersons && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                {/* @ts-ignore */}
                {errors?.contactPersons?.some((x) => x?.email?.type === "email") ? (
                  <PaziaErrorMessage>Niepoprawny adres email w jednym z pól.</PaziaErrorMessage>
                ) : (
                  <PaziaErrorMessage>Uzupełnij wszystkie wymagane pola.</PaziaErrorMessage>
                )}
              </div>
            )}
          </div>

          {/* Add new contact person button */}
          <div className="flex justify-center">
            <PaziaButton
              onClick={() => append({ area: "", phone: "", email: "", userId: 0 })}
              type="primary"
              btnType="button"
              className="inline-flex items-center gap-2"
            >
              Dodaj osobę kontaktową
            </PaziaButton>
          </div>

          {/* Form actions */}
          <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end dark:border-slate-700">
            <button
              type="button"
              className="order-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 sm:order-1 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              Anuluj
            </button>
            <PaziaFormButton title="Zapisz osoby kontaktowe" type="success" className="order-1 sm:order-2" />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export const CONTACT_PERSONS_FORM_FIELDS = {
  AREA: "area",
  PHONE: "phone",
  EMAIL: "email",
  USER_ID: "userId",
} as const;
export const schema = yup.object({
  contactPersons: yup
    .array()
    .of(
      yup.object({
        [CONTACT_PERSONS_FORM_FIELDS.AREA]: yup.string().required(),
        [CONTACT_PERSONS_FORM_FIELDS.PHONE]: yup.string().required(),
        [CONTACT_PERSONS_FORM_FIELDS.EMAIL]: yup.string().email().required(),
        [CONTACT_PERSONS_FORM_FIELDS.USER_ID]: yup.number().required(),
      }),
    )
    .required(),
});

export type CourseGroupContactPersonsValues = yup.InferType<typeof schema>;
